import { useCallback, useEffect, useRef, useState } from 'react'
import { reaction } from 'mobx'
import {
	fetchEssayVersions,
	fetchEssayVersionsMetadata,
	fetchVersionChatMessages,
	markEssayVersionSeen,
	patchVersionChatReadForCounselor,
} from 'api/essays'
import { fetchEssaySummary } from 'api/essaySummary'
import {
	areFeedbackItemsEqual,
	mapMessagesToFeedbackItems,
} from 'pages/Dashboard/components/TodaysPrioritySection/utils/feedbackMapping'
import {
	buildApplicationDrawerItem,
	buildEssayListItems,
	contentSnapshotFromVersion,
	resolveEssayThreadTitle,
	essaySummarySnapshot,
	metadataUnreadTotal,
	normalizeVersionMetadataList,
	snapshotsContentEqual,
	snapshotsEqual,
	summaryHasDrawerFields,
} from 'shared/essayReview/essayReviewModel'
import { sortEssaysByPriority } from '../utils/essayStatusPriority'
import { essaySummaryStore } from '../model/essaySummaryStore'
import {
	syncEssayVersionResolved,
	syncFeedbackThreadSeen,
} from 'shared/utils/counselorSeenSync'

const SESSION_CACHE_LIMIT = 5
const inflightEssay = new Map()
const inflightVersion = new Map()

function runDeduped(map, key, factory) {
	if (map.has(key)) return map.get(key)
	const promise = factory().finally(() => {
		map.delete(key)
	})
	map.set(key, promise)
	return promise
}

export function useApplicationEssayReviewDrawer() {
	const [open, setOpen] = useState(false)
	const [row, setRow] = useState(null)
	const [essays, setEssays] = useState([])
	const [essayIndex, setEssayIndex] = useState(0)
	const [activeVersionId, setActiveVersionId] = useState('')
	const [version, setVersion] = useState(null)
	const [versionsMeta, setVersionsMeta] = useState([])
	const [metadataByEssayId, setMetadataByEssayId] = useState({})
	const [loading, setLoading] = useState(false)
	const [feedbackItems, setFeedbackItems] = useState([])
	const [feedbackLoading, setFeedbackLoading] = useState(false)
	const [unreadStudentCount, setUnreadStudentCount] = useState(0)
	const [listPanelOpen, setListPanelOpen] = useState(false)
	const [pendingUpdate, setPendingUpdate] = useState(null)
	const [listLoading, setListLoading] = useState(false)

	const sessionCacheRef = useRef({})
	const lruRef = useRef([])
	const generationRef = useRef(0)
	const abortRef = useRef(null)
	const contentSnapshotRef = useRef(null)
	const summarySnapshotByEssayRef = useRef({})

	const touchCache = useCallback(essayId => {
		const id = String(essayId || '')
		if (!id) return
		lruRef.current = [id, ...lruRef.current.filter(x => x !== id)].slice(
			0,
			SESSION_CACHE_LIMIT,
		)
		while (lruRef.current.length > SESSION_CACHE_LIMIT) {
			const evict = lruRef.current.pop()
			if (evict) delete sessionCacheRef.current[evict]
		}
	}, [])

	const invalidateEssayCache = useCallback(essayId => {
		const id = String(essayId || '')
		if (!id) return
		delete sessionCacheRef.current[id]
		lruRef.current = lruRef.current.filter(x => x !== id)
	}, [])

	const clearSession = useCallback(() => {
		sessionCacheRef.current = {}
		lruRef.current = []
		summarySnapshotByEssayRef.current = {}
		contentSnapshotRef.current = null
	}, [])

	const getSummaryEssay = useCallback(
		essayId => {
			const id = String(essayId || '')
			return (essaySummaryStore.essays || []).find(e => String(e.id) === id)
		},
		[],
	)

	const markEssayOpened = useCallback((essay, versionId) => {
		const summary = essay || null
		const vid = String(
			versionId || summary?.displayVersionId || '',
		).trim()
		const eid = String(summary?.id || '').trim()
		if (!eid && !vid) return

		essaySummaryStore.markEssaySeen(eid, { versionId: vid })
		if (vid) {
			markEssayVersionSeen(vid, {
				anchorAt:
					summary?.latestVersionUpdatedAt ||
					summary?.updatedAt ||
					undefined,
			}).catch(() => {})
		}
	}, [])

	const loadFeedback = useCallback(
		async (essayId, versionId, versionRow, { markRead = true } = {}) => {
			const key = `fb:${essayId}:${versionId}:read:${markRead ? 1 : 0}`
			return runDeduped(inflightVersion, key, async () => {
				const res = await fetchVersionChatMessages(essayId, versionId)
				const messages = Array.isArray(res?.data?.messages)
					? res.data.messages
					: []
				const unread = messages.filter(
					m => m?.sender_type === 'student' && !m?.is_read_by_counselor,
				).length
				const items = mapMessagesToFeedbackItems(
					messages,
					versionRow?.content_text || '',
				)
				if (markRead) {
					await patchVersionChatReadForCounselor(essayId, versionId).catch(
						() => {},
					)
					syncFeedbackThreadSeen({
						versionId,
						essayId,
						unreadMessageCount: unread,
					})
				}
				return { items, unread }
			})
		},
		[],
	)

	const loadEssayContent = useCallback(
		async (essay, { versionId: preferredVersionId, force = false } = {}) => {
			const essayId = String(essay?.id || '')
			if (!essayId) return

			const gen = ++generationRef.current
			abortRef.current?.abort?.()
			const controller = new AbortController()
			abortRef.current = controller

			const summaryEssay = getSummaryEssay(essayId) || essay
			const targetVersionId =
				String(preferredVersionId || '') ||
				String(summaryEssay?.displayVersionId || '')

			const cached = sessionCacheRef.current[essayId]
			const summarySnap = essaySummarySnapshot(summaryEssay)
			if (
				!force &&
				cached?.snapshot &&
				snapshotsEqual(cached.snapshot, summarySnap) &&
				cached.versionsById?.[targetVersionId]
			) {
				touchCache(essayId)
				if (gen !== generationRef.current) return
				const v = cached.versionsById[targetVersionId]
				setVersionsMeta(cached.metadata || [])
				setVersion(v)
				setActiveVersionId(targetVersionId)
				setUnreadStudentCount(
					cached.feedback?.[targetVersionId]?.unread ??
						metadataUnreadTotal(cached.metadata),
				)
				setFeedbackItems(cached.feedback?.[targetVersionId]?.items || [])
				setFeedbackLoading(false)
				setLoading(false)
				contentSnapshotRef.current = contentSnapshotFromVersion(
					v,
					resolveEssayThreadTitle(summaryEssay),
				)
				markEssayOpened(summaryEssay, targetVersionId)
				return
			}

			const softRefresh = Boolean(force && contentSnapshotRef.current)
			if (!softRefresh) {
				setLoading(true)
				setFeedbackLoading(true)
				setFeedbackItems([])
				setUnreadStudentCount(0)
			}

			try {
				const needsMeta =
					force ||
					!cached?.metadata?.length ||
					!snapshotsEqual(cached?.snapshot, summarySnap)

				let meta = cached?.metadata || []
				const shouldFetchMeta =
					needsMeta && (!summaryHasDrawerFields(summaryEssay) || !meta.length)
				if (shouldFetchMeta) {
					const metaRes = await fetchEssayVersionsMetadata(essayId, {
						includeDrafts: true,
					})
					if (controller.signal.aborted || gen !== generationRef.current) return
					meta = normalizeVersionMetadataList(metaRes?.data?.versions)
				}

				const versionsRes = await runDeduped(
					inflightEssay,
					`full:${essayId}`,
					() => fetchEssayVersions(essayId, { includeDrafts: true }),
				)
				if (controller.signal.aborted || gen !== generationRef.current) return

				const versionRows = Array.isArray(versionsRes?.data?.versions)
					? versionsRes.data.versions
					: []
				const versionsById = {}
				versionRows.forEach(v => {
					if (v?.id) versionsById[String(v.id)] = v
				})

				const pickId =
					targetVersionId && versionsById[targetVersionId]
						? targetVersionId
						: String(versionRows[0]?.id || '')
				const picked = versionsById[pickId] || null

				if (!meta.length) {
					meta = normalizeVersionMetadataList(versionRows)
				}

				const fb = await loadFeedback(essayId, pickId, picked, {
					markRead: !softRefresh,
				})
				if (controller.signal.aborted || gen !== generationRef.current) return

				sessionCacheRef.current[essayId] = {
					snapshot: summarySnap,
					metadata: meta,
					versionsById,
					feedback: { [pickId]: fb },
				}
				touchCache(essayId)

				setVersionsMeta(meta)
				if (!softRefresh) {
					setVersion(picked)
					setActiveVersionId(pickId)
					setFeedbackItems(fb.items)
					setUnreadStudentCount(fb.unread)
					contentSnapshotRef.current = contentSnapshotFromVersion(
						picked,
						resolveEssayThreadTitle(summaryEssay),
					)
				} else {
					setFeedbackItems(prev =>
						areFeedbackItemsEqual(prev, fb.items) ? prev : fb.items,
					)
					setUnreadStudentCount(fb.unread)
				}
				markEssayOpened(summaryEssay, pickId)
			} catch (e) {
				if (!controller.signal.aborted) {
					console.error(e)
					if (!softRefresh) {
						setVersion(null)
						setFeedbackItems([])
					}
				}
			} finally {
				if (gen === generationRef.current) {
					if (!softRefresh) {
						setLoading(false)
						setFeedbackLoading(false)
					}
				}
			}
		},
		[getSummaryEssay, loadFeedback, touchCache, markEssayOpened],
	)

	const openDrawer = useCallback(
		(applicationRow, essay) => {
			const list = sortEssaysByPriority(applicationRow?.essays || [])
			const idx = Math.max(
				0,
				list.findIndex(e => String(e.id) === String(essay?.id)),
			)
			setRow(applicationRow)
			setEssays(list)
			setEssayIndex(idx)
			setOpen(true)
			setListPanelOpen(false)
			setPendingUpdate(null)
			const target = list[idx] || essay
			const summary = getSummaryEssay(target?.id) || target
			markEssayOpened(
				summary,
				summary?.displayVersionId || target?.displayVersionId,
			)
			loadEssayContent(target)
		},
		[getSummaryEssay, loadEssayContent, markEssayOpened],
	)

	const closeDrawer = useCallback(() => {
		generationRef.current += 1
		abortRef.current?.abort?.()
		setOpen(false)
		setRow(null)
		setEssays([])
		setListPanelOpen(false)
		setPendingUpdate(null)
		clearSession()
	}, [clearSession])

	const navigateEssay = useCallback(
		nextIndex => {
			const next = essays[nextIndex]
			if (!next) return
			setEssayIndex(nextIndex)
			setPendingUpdate(null)
			const summary = getSummaryEssay(next.id) || next
			markEssayOpened(summary, summary?.displayVersionId)
			loadEssayContent(next)
		},
		[essays, getSummaryEssay, loadEssayContent, markEssayOpened],
	)

	const selectVersion = useCallback(
		async versionId => {
			const essay = essays[essayIndex]
			if (!essay) return
			const essayId = String(essay.id)
			const vid = String(versionId || '')
			if (!vid) return

			const gen = ++generationRef.current
			setActiveVersionId(vid)

			const cached = sessionCacheRef.current[essayId]
			if (cached?.versionsById?.[vid]) {
				const v = cached.versionsById[vid]
				setVersion(v)
				setLoading(false)
				if (cached.feedback?.[vid]) {
					setFeedbackItems(cached.feedback[vid].items)
					setUnreadStudentCount(cached.feedback[vid].unread)
					const summary = getSummaryEssay(essay.id) || essay
					contentSnapshotRef.current = contentSnapshotFromVersion(
						v,
						resolveEssayThreadTitle({
							title: summary?.title || essay?.title,
						}),
					)
					return
				}
			}

			setLoading(true)
			setFeedbackLoading(true)
			try {
				let v = cached?.versionsById?.[vid]
				if (!v) {
					const versionsRes = await fetchEssayVersions(essayId, {
						includeDrafts: true,
					})
					if (gen !== generationRef.current) return
					const versionRows = Array.isArray(versionsRes?.data?.versions)
						? versionsRes.data.versions
						: []
					v = versionRows.find(r => String(r.id) === vid) || null
					if (v && cached) {
						cached.versionsById = cached.versionsById || {}
						cached.versionsById[vid] = v
					}
				}
				setVersion(v)
				const fb = await loadFeedback(essayId, vid, v)
				if (gen !== generationRef.current) return
				if (cached) {
					cached.feedback = cached.feedback || {}
					cached.feedback[vid] = fb
				}
				setFeedbackItems(fb.items)
				setUnreadStudentCount(fb.unread)
				const summary = getSummaryEssay(essay.id) || essay
				contentSnapshotRef.current = contentSnapshotFromVersion(
					v,
					resolveEssayThreadTitle(summary),
				)
			} catch (e) {
				console.error(e)
			} finally {
				if (gen === generationRef.current) {
					setLoading(false)
					setFeedbackLoading(false)
				}
			}
		},
		[essays, essayIndex, getSummaryEssay, loadFeedback],
	)

	const loadAllMetadata = useCallback(async () => {
		if (!essays.length) return
		setListLoading(true)
		try {
			const entries = await Promise.all(
				essays.map(async e => {
					const cached = sessionCacheRef.current[e.id]
					if (cached?.metadata?.length) {
						return [e.id, cached.metadata]
					}
					const res = await fetchEssayVersionsMetadata(e.id, {
						includeDrafts: true,
					})
					const meta = normalizeVersionMetadataList(res?.data?.versions)
					return [e.id, meta]
				}),
			)
			const map = {}
			entries.forEach(([id, meta]) => {
				map[id] = {
					unreadTotal: metadataUnreadTotal(meta),
					versionCount: meta.length,
				}
				const cached = sessionCacheRef.current[id]
				if (cached) cached.metadata = meta
			})
			setMetadataByEssayId(map)
		} catch (e) {
			console.error(e)
		} finally {
			setListLoading(false)
		}
	}, [essays])

	const toggleListPanel = useCallback(() => {
		setListPanelOpen(prev => {
			const next = !prev
			if (next) loadAllMetadata()
			return next
		})
	}, [loadAllMetadata])

	const selectListEssay = useCallback(
		essayIdOrItem => {
			const essayId =
				essayIdOrItem && typeof essayIdOrItem === 'object'
					? essayIdOrItem.id
					: essayIdOrItem
			const idx = essays.findIndex(e => String(e.id) === String(essayId))
			if (idx < 0) return
			setListPanelOpen(false)
			navigateEssay(idx)
		},
		[essays, navigateEssay],
	)

	const applyPendingUpdate = useCallback(() => {
		if (!pendingUpdate?.version) return
		setVersion(pendingUpdate.version)
		setFeedbackItems(pendingUpdate.feedbackItems || [])
		setUnreadStudentCount(pendingUpdate.unread || 0)
		contentSnapshotRef.current = pendingUpdate.snapshot
		setPendingUpdate(null)
	}, [pendingUpdate])

	const refreshSummary = useCallback(async () => {
		try {
			const payload = await fetchEssaySummary()
			essaySummaryStore.setPayload(payload)
		} catch (e) {
			console.error(e)
		}
	}, [])

	useEffect(() => {
		if (!open) return undefined
		const dispose = reaction(
			() => ({
				essays: essaySummaryStore.essays.map(essaySummarySnapshot),
				updatedAt: essaySummaryStore.updatedAt,
			}),
			() => {
				const activeEssay = essays[essayIndex]
				if (!activeEssay) return
				const fresh = getSummaryEssay(activeEssay.id)
				if (!fresh) return

				const prevSnap =
					summarySnapshotByEssayRef.current[activeEssay.id] ||
					sessionCacheRef.current[activeEssay.id]?.snapshot
				const nextSnap = essaySummarySnapshot(fresh)
				summarySnapshotByEssayRef.current[activeEssay.id] = nextSnap

				if (!snapshotsEqual(prevSnap, nextSnap)) {
					invalidateEssayCache(activeEssay.id)
				}

				essays.forEach(e => {
					const f = getSummaryEssay(e.id)
					const p = summarySnapshotByEssayRef.current[e.id]
					const n = essaySummarySnapshot(f || e)
					if (!snapshotsEqual(p, n)) {
						invalidateEssayCache(e.id)
						summarySnapshotByEssayRef.current[e.id] = n
					}
				})

				if (!snapshotsEqual(prevSnap, nextSnap)) {
					const cached = sessionCacheRef.current[activeEssay.id]
					const vid =
						activeVersionId ||
						fresh.displayVersionId ||
						cached?.versionsById?.[activeVersionId]
							? activeVersionId
							: ''
					if (contentSnapshotRef.current && !loading) {
						loadEssayContent(fresh, {
							versionId: vid,
							force: true,
						}).then(() => {
							const current = sessionCacheRef.current[activeEssay.id]
							const v =
								current?.versionsById?.[activeVersionId] || version
							const nextContent = contentSnapshotFromVersion(
								v,
								resolveEssayThreadTitle(fresh),
							)
							if (
								!snapshotsContentEqual(
									contentSnapshotRef.current,
									nextContent,
								)
							) {
								setPendingUpdate({
									version: v,
									feedbackItems:
										current?.feedback?.[activeVersionId]?.items ||
										feedbackItems,
									unread:
										current?.feedback?.[activeVersionId]?.unread ||
										unreadStudentCount,
									snapshot: nextContent,
								})
							}
						})
					}
				}
			},
		)
		return dispose
	}, [
		open,
		essays,
		essayIndex,
		activeVersionId,
		getSummaryEssay,
		invalidateEssayCache,
		loadEssayContent,
		loading,
		version,
		feedbackItems,
		unreadStudentCount,
	])

	const activeEssay = essays[essayIndex] || null

	const handleResolvePending = useCallback(() => {
		const vid = String(activeVersionId || version?.id || '').trim()
		const eid = String(activeEssay?.id || '').trim()
		if (vid) {
			syncEssayVersionResolved({ versionId: vid, essayId: eid })
		}
		refreshSummary()
	}, [activeVersionId, version, activeEssay, refreshSummary])

	const reloadFeedback = useCallback(
		({ silent = false } = {}) => {
			if (!activeEssay || !activeVersionId) return Promise.resolve()
			if (!silent) setFeedbackLoading(true)
			return loadFeedback(activeEssay.id, activeVersionId, version, {
				markRead: !silent,
			})
				.then(fb => {
					setFeedbackItems(prev =>
						areFeedbackItemsEqual(prev, fb.items) ? prev : fb.items,
					)
					setUnreadStudentCount(fb.unread)
					const essayId = String(activeEssay.id)
					const cached = sessionCacheRef.current[essayId]
					if (cached) {
						cached.feedback = cached.feedback || {}
						cached.feedback[activeVersionId] = fb
					}
				})
				.catch(e => {
					console.error(e)
					if (!silent) {
						setFeedbackItems([])
						setUnreadStudentCount(0)
					}
				})
				.finally(() => {
					if (!silent) setFeedbackLoading(false)
				})
		},
		[activeEssay, activeVersionId, loadFeedback, version],
	)

	useEffect(() => {
		if (!open || !activeEssay?.id || !activeVersionId) return undefined
		const id = window.setInterval(() => {
			reloadFeedback({ silent: true })
		}, 15000)
		return () => window.clearInterval(id)
	}, [open, activeEssay?.id, activeVersionId, reloadFeedback])

	const summaryActive = activeEssay
		? (() => {
				const fromStore = getSummaryEssay(activeEssay.id)
				return {
					...(fromStore || activeEssay),
					title: resolveEssayThreadTitle({
						title: fromStore?.title || activeEssay?.title,
					}),
				}
			})()
		: null
	const activeMeta = versionsMeta.find(
		v => String(v.id) === String(activeVersionId),
	)
	const item = summaryActive
		? buildApplicationDrawerItem({
				essay: summaryActive,
				row,
				versionMeta: activeMeta,
				versionRow: version,
			})
		: null
	const drawerItems = essays.map(e => {
		const fromStore = getSummaryEssay(e.id)
		const summary = {
			...(fromStore || e),
			title: resolveEssayThreadTitle({
				title: fromStore?.title || e?.title,
			}),
		}
		const isActive = String(e.id) === String(summaryActive?.id)
		return buildApplicationDrawerItem({
			essay: summary,
			row,
			versionMeta: isActive ? activeMeta : null,
			versionRow: isActive ? version : null,
		})
	})

	const listPanelItems = buildEssayListItems(
		essays.map(e => {
			const fromStore = getSummaryEssay(e.id)
			return {
				...(fromStore || e),
				title: resolveEssayThreadTitle({
					title: fromStore?.title || e?.title,
				}),
			}
		}),
		metadataByEssayId,
	)

	return {
		open,
		openDrawer,
		closeDrawer,
		row,
		item,
		drawerItems,
		essayIndex,
		navigateEssay,
		version,
		versionsMeta,
		activeVersionId,
		selectVersion,
		loading,
		feedbackItems,
		feedbackLoading,
		unreadStudentCount,
		setFeedbackItems,
		setUnreadStudentCount,
		reloadFeedback,
		listPanelOpen,
		toggleListPanel,
		listPanelItems,
		listLoading,
		selectListEssay,
		pendingUpdate,
		applyPendingUpdate,
		refreshSummary,
		handleResolvePending,
		activeEssayId: activeEssay?.id,
	}
}
